import { z } from 'zod';

// ---------------------------------------------------------------------------
// GitHub personal access token (classic)
//
// kruxit has no database, so a user's connected GitHub PAT is split across Clerk
// metadata: the encrypted token lives in server-only `privateMetadata`, while a
// non-sensitive status (login, last 4, granted scopes, when added) lives in
// `publicMetadata` so the client can render connection state via `useUser()`
// without an extra round-trip. The plaintext token is never persisted.
//
// Classic (not fine-grained) is required: a classic `repo` token reads a user's
// private org repos without the org having to approve a fine-grained token.
// ---------------------------------------------------------------------------

/**
 * Scopes the current functionality needs — `missingScopes` warns only when one
 * of these is absent.
 */
export const REQUIRED_SCOPES = ['repo', 'read:org', 'read:user'] as const;
export type RequiredScope = (typeof REQUIRED_SCOPES)[number];

/**
 * Scopes requested ahead of planned features (GitHub Projects, notifications,
 * webhooks). Surfaced to the user as recommended, but **not** enforced — their
 * absence is never flagged as missing.
 */
export const PLANNED_SCOPES = [
  'read:project',
  'notifications',
  'read:repo_hook',
] as const;

/**
 * A classic PAT. GitHub prefixes classic tokens with `ghp_`; fine-grained
 * (`github_pat_`), OAuth (`gho_`) and App (`ghu_`/`ghs_`/`ghr_`) tokens use other
 * prefixes and so are rejected. The pre-2021 prefix-less 40-hex format is no
 * longer accepted — token type is identified by prefix.
 */
function isClassicPersonalAccessToken(token: string): boolean {
  return token.startsWith('ghp_');
}

/** Request body for `POST /api/clerk/github-token`. */
export const SetGithubPatInputSchema = z.object({
  token: z
    .string()
    .trim()
    .min(8)
    .max(255)
    .refine(isClassicPersonalAccessToken, {
      message:
        'Enter a classic GitHub personal access token (it starts with "ghp_"). Fine-grained, OAuth and GitHub App tokens are not supported.',
    }),
});
export type SetGithubPatInput = z.infer<typeof SetGithubPatInputSchema>;

// ── Scope hierarchy ────────────────────────────────────────────────────────
// GitHub broader scopes imply narrower ones, so an exact-string check would
// wrongly warn that e.g. `admin:org` is "missing read:org". Map each broad scope
// to the scopes it also grants.
const SCOPE_IMPLICATIONS = new Map<string, readonly string[]>([
  ['admin:org', ['write:org', 'read:org']],
  ['write:org', ['read:org']],
  ['user', ['read:user', 'user:email', 'user:follow']],
]);

function expandGrantedScopes(granted: readonly string[]): ReadonlySet<string> {
  const expanded = new Set<string>();
  for (const scope of granted) {
    expanded.add(scope);
    for (const implied of SCOPE_IMPLICATIONS.get(scope) ?? []) {
      expanded.add(implied);
    }
  }
  return expanded;
}

/** The required scopes not covered (directly or by implication) by `granted`. */
export function missingScopes(
  granted: readonly string[],
): readonly RequiredScope[] {
  const expanded = expandGrantedScopes(granted);
  return REQUIRED_SCOPES.filter((required) => !expanded.has(required));
}

/** True when `granted` covers every required scope. */
export function hasRequiredScopes(granted: readonly string[]): boolean {
  return missingScopes(granted).length === 0;
}

// ── Stored shapes ────────────────────────────────────────────────────────────

/** Server-only `privateMetadata.githubPat`: the encrypted token + its KMS key. */
export const StoredGithubPatSchema = z.object({
  ciphertext: z.string().min(1),
  keyId: z.string().min(1),
});
export type StoredGithubPat = z.infer<typeof StoredGithubPatSchema>;

/** Client-visible `publicMetadata.githubPatStatus`: no secret material. */
export const GithubPatStatusSchema = z.object({
  login: z.string(),
  last4: z.string(),
  scopes: z.array(z.string()),
  addedAt: z.string(),
});
export type GithubPatStatus = z.infer<typeof GithubPatStatusSchema>;

/**
 * Read the connected-PAT status from Clerk `publicMetadata`, or `undefined` when
 * absent/malformed (mirrors `planFromMetadata`).
 */
export function readGithubPatStatus(
  metadata: unknown,
): GithubPatStatus | undefined {
  const status = (
    metadata as { readonly githubPatStatus?: unknown } | null | undefined
  )?.githubPatStatus;
  const parsed = GithubPatStatusSchema.safeParse(status);
  return parsed.success ? parsed.data : undefined;
}
